import { create } from 'zustand';
import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';
import { toast } from 'react-hot-toast';

interface Price {
  id: string;
  nickname?: string;
  unit_amount: number;
  currency: string;
  type?: 'recurring' | 'metered';
  recurring?: {
    interval: string;
  };
}

interface SubscriptionState {
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  prices: Price[] | null;
  usageCount: number; // Unified usage count (from Stripe for paid, from local for trial)
  freeUsageCount: number; // Free trial usage count
  standardApplicationPrice: Price | null;
  premiumApplicationPrice: Price | null;
  currentPeriodEnd: Date | null;
  isModalOpen: boolean;
  status: string | null;
  freeTrialLimit: number;
  freeTrialRemaining: number;
  checkSubscription: () => Promise<void>;
  createSubscription: () => Promise<string>;
  cancelSubscription: () => Promise<boolean>;
  openSubscriptionModal: () => void;
  closeSubscriptionModal: () => void;
}

// Define free trial limit constant
export const FREE_TRIAL_LIMIT = 20;

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  isSubscribed: false,
  isLoading: false,
  error: null,
  prices: null,
  usageCount: 0,
  freeUsageCount: 0,
  standardApplicationPrice: null,
  premiumApplicationPrice: null,
  currentPeriodEnd: null,
  isModalOpen: false,
  status: null,
  freeTrialLimit: FREE_TRIAL_LIMIT,
  freeTrialRemaining: FREE_TRIAL_LIMIT,

  checkSubscription: async () => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.get('/api/subscription/status');

      const usageCount = response.data.usageCount || 0;
      const freeUsageCount = response.data.freeUsageCount || 0;
      const freeTrialRemaining = response.data.freeTrialRemaining || 0;
      const isSubscribed = response.data.isSubscribed || false;

      logger.info('Subscription', 'Subscription status checked', {
        isSubscribed,
        usageCount,
        freeUsageCount,
        freeTrialRemaining,
        status: response.data.status,
        endpoint: '/api/subscription/status',
      });

      set({
        isLoading: false,
        isSubscribed,
        usageCount,
        freeUsageCount,
        currentPeriodEnd: response.data.currentPeriodEnd
          ? new Date(response.data.currentPeriodEnd)
          : null,
        status: response.data.status,
        standardApplicationPrice: response.data.standardApplicationPrice,
        premiumApplicationPrice: response.data.premiumApplicationPrice,
        freeTrialRemaining,
      });
    } catch (error) {
      logger.error('Subscription', 'Failed to check subscription', {
        error,
      });
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : 'An error occurred while checking subscription',
      });
    }
  },

  createSubscription: async () => {
    try {
      const response = await api.post('/api/subscription/create-checkout');
      return response.data.url;
    } catch (error) {
      logger.error('Subscription', 'Failed to create checkout session', {
        error,
      });
      set({
        error:
          error instanceof Error ? error.message : 'An error occurred while creating subscription',
      });
      throw error;
    }
  },

  cancelSubscription: async () => {
    logger.info('SubscriptionStore', 'Initiating subscription cancellation');
    set({ isLoading: true, error: null });
    try {
      await api.post('/api/subscription/cancel');

      logger.info(
        'SubscriptionStore',
        'Subscription cancellation request successful. Refreshing status.'
      );
      // Refresh the subscription status from Stripe
      await get().checkSubscription();

      set({ isLoading: false });
      toast.success('Subscription successfully scheduled for cancellation.');
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('SubscriptionStore', 'Failed to cancel subscription', {
        error: errorMessage,
        responseData: (error as any)?.response?.data,
      });
      set({
        isLoading: false,
        error: `Failed to cancel subscription: ${errorMessage}`,
      });
      toast.error(`Failed to cancel subscription: ${errorMessage}`);
      return false;
    }
  },

  openSubscriptionModal: () => {
    set({ isModalOpen: true });
  },

  closeSubscriptionModal: () => {
    set({ isModalOpen: false });
  },
}));
